// apps/dashboard/src/api/utils/time.ts
// Pure utility: formatRelativeTime(timestampMs) -> human-readable relative time.
// FR-006: Used by <RelativeTime> to display timestamps on refinement run cards.
// FR-023: Locale-aware via getLocale() for date/time/number formatting.
// Never throws. Returns em-dash for invalid/null/undefined/NaN/non-finite input.

import { getLocale } from "../../i18n/getLocale";

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Converts a millisecond timestamp into a human-readable relative time string.
 *
 * Uses Intl.RelativeTimeFormat when the active locale is not en-US, falling
 * back to the abbreviated English format for en-US (backwards-compatible).
 *
 * - < 60 s ago (or future): "just now"
 * - 1–59 minutes ago: "<n>m ago" (en-US) or locale-relative
 * - 1–23 hours ago: "<n>h ago" (en-US) or locale-relative
 * - >= 24 hours ago: locale date string via Intl.DateTimeFormat
 * - null / undefined / NaN / Infinity: em-dash "\u2014"
 */
export function formatRelativeTime(timestampMs: number): string {
  if (
    timestampMs == null ||
    typeof timestampMs !== "number" ||
    !Number.isFinite(timestampMs)
  ) {
    return "\u2014";
  }

  const now = Date.now();
  const diffMs = now - timestampMs;
  const locale = getLocale();

  // Future timestamps or less than 60 seconds ago
  if (diffMs < MINUTE_MS) {
    return "just now";
  }

  // 1–59 minutes ago
  if (diffMs < HOUR_MS) {
    const minutes = Math.floor(diffMs / MINUTE_MS);
    if (locale === "en-US") {
      return `${minutes}m ago`;
    }
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
      -minutes,
      "minute",
    );
  }

  // 1–23 hours ago
  if (diffMs < DAY_MS) {
    const hours = Math.floor(diffMs / HOUR_MS);
    if (locale === "en-US") {
      return `${hours}h ago`;
    }
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
      -hours,
      "hour",
    );
  }

  // >= 24 hours ago: locale date string
  return new Intl.DateTimeFormat(locale).format(new Date(timestampMs));
}

/**
 * Formats a number using the active locale.
 *
 * @example formatNumber(1234.56) // "1,234.56" (en-US) or "1.234,56" (es-CL)
 */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(getLocale(), options).format(value);
}

/**
 * Formats a currency value using the active locale.
 *
 * @example formatCurrency(42.5, "USD") // "$42.50" (en-US) or "US$42,50" (es-CL)
 */
export function formatCurrency(
  value: number,
  currency = "USD",
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(getLocale(), {
    style: "currency",
    currency,
    ...options,
  }).format(value);
}

/**
 * Formats a date using the active locale.
 *
 * @example formatDate(Date.now()) // "5/21/2026" (en-US) or "21-05-2026" (es-CL)
 */
export function formatDate(
  timestampMs: number,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!Number.isFinite(timestampMs)) return "\u2014";
  return new Intl.DateTimeFormat(getLocale(), options).format(
    new Date(timestampMs),
  );
}
